import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Divider, Grid, Typography} from "@mui/material";
import {CreditCard} from "@mui/icons-material";
import Busy from "../Busy";
import {requestPaymentMethods} from "./request/payment-method-requests";
import {isPaymentMethodExpired} from "../../util/PaymentUtils";
import PaymentMethodsList from "./PaymentMethodsList";
import PropTypes from "prop-types";
import VerifyBankAccountDialog from "./VerifyBankAccountDialog";
import AddPaymentMethodFlowManager from "./AddPaymentMethodFlowManager";
import RenamePaymentMethodDialog from "./RenamePaymentMethodDialog";
import RemovePaymentMethodDialog from "./RemovePaymentMethodDialog";
import {withSnackbar} from "../hocs/withSnackbar";
import EmailVerifyToolTip from "../notifications/EmailVerifyToolTip";
import {styled} from "@mui/material/styles";

const Gap15 = styled(Grid)(() => ({
  gap: '1.07rem'
}));

const AddPaymentButton = styled(Button)(() => ({
  textTransform: 'uppercase'
}));

const BuyerPaymentMethods = ({account, snackbarShowMessage}) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentToVerify, setPaymentToVerify] = useState(null);
  const [paymentToEdit, setPaymentToEdit] = useState(null);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showRenamePaymentDialog, setShowRenamePaymentDialog] = useState(false);
  const [showDeletePaymentDialog, setShowDeletePaymentDialog] = useState(false);

  const loadPaymentMethods = useCallback(() => {
    Busy.set(true);
    requestPaymentMethods(
      account.authorityId || account.id,
      (response) => {
        setPaymentMethods(response);
        Busy.set(false);
      },
      (error) => {
        Busy.set(false);
        snackbarShowMessage(error, 'error', 15000);
      }
    );
  }, [account, snackbarShowMessage]);

  useEffect(() => {
    loadPaymentMethods();
  }, [loadPaymentMethods]);

  const openAddPaymentModal = () => {
    setShowAddPaymentModal(true);
  };

  const handlePaymentVerificationSuccess = () => {
    setPaymentToVerify(null);
    loadPaymentMethods();
  };

  const closePaymentVerificationDialog = () => {
    setPaymentToVerify(null);
  };

  const handlePaymentVerification = useCallback(function handlePaymentVerification(paymentMethod) {
    setPaymentToVerify({...paymentMethod});
  }, [setPaymentToVerify]);

  const closeRenamePaymentDialog = () => {
    setShowRenamePaymentDialog(false);
    setPaymentToEdit(null);
  };

  const handlePaymentRename = () => {
    setPaymentToEdit(null);
    setShowRenamePaymentDialog(false);
    snackbarShowMessage('Payment method nick name updated successfully', 'success', 8000);
    loadPaymentMethods();
  };

  const handleCloseRemovePaymentDialog = () => {
    setShowDeletePaymentDialog(false);
    setPaymentToEdit(null);
  };

  const handlePaymentRemove = () => {
    setPaymentToEdit(null);
    setShowDeletePaymentDialog(false);
    snackbarShowMessage('Payment method removed successfully', 'success', 8000);
    loadPaymentMethods();
  };

  const handlePaymentMenuAction = useCallback((actionName, paymentMethod) => {
    setPaymentToEdit({...paymentMethod});
    switch (actionName) {
      case 'rename':
        setShowRenamePaymentDialog(true);
        break;
      case 'remove':
        setShowDeletePaymentDialog(true);
        break;
      default:
        break;
    }
  }, [setPaymentToEdit, setShowRenamePaymentDialog, setShowDeletePaymentDialog]);

  /**
   *  Cards are valid through the end of their expiration month
   *  e.g., if today is 12/2025 and the card expires on 12/2025
   *  the card is still valid.
   *  Only cards with expiration dates BEFORE the current month are expired
   */
  const { expiredMethods, unexpiredMethods } = useMemo(() => {
    const expired = [];
    const unexpired = [];

    paymentMethods.forEach((method) => {
      if (isPaymentMethodExpired(method)) {
        expired.push(method);
      } else {
        unexpired.push(method);
      }
    });

    return { expiredMethods: expired, unexpiredMethods: unexpired };
  }, [paymentMethods]);

  return (
    <Gap15 container direction={'column'}>
      <Gap15 item container justifyContent={'space-between'}>
        <Typography variant={'h5'} component={'h1'}>Payment Method</Typography>
        <EmailVerifyToolTip
          title={"Please verify your email address to add a payment method."}
          isEmailVerified={account?.emailVerified}
        >
          <AddPaymentButton
            onClick={openAddPaymentModal}
            color='secondary'
            startIcon={<CreditCard/>}
            variant='contained'
            disabled={!account?.emailVerified}
          >
            Add Payment
          </AddPaymentButton>
        </EmailVerifyToolTip>
      </Gap15>
      <PaymentMethodsList
        paymentMethods={unexpiredMethods}
        onMenuAction={handlePaymentMenuAction}
        verifyPaymentMethod={handlePaymentVerification}
      />
      {
        expiredMethods.length > 0 && <>
          <Divider />
          <Typography variant={'h6'}>Expired Payment Methods</Typography>
          <PaymentMethodsList
            paymentMethods={expiredMethods}
            onMenuAction={handlePaymentMenuAction}
            verifyPaymentMethod={handlePaymentVerification}
          />
        </>
      }
      <AddPaymentMethodFlowManager
        account={account}
        setOpen={setShowAddPaymentModal}
        open={showAddPaymentModal}
        loadPaymentMethods={loadPaymentMethods}
      />
      <VerifyBankAccountDialog
        open={paymentToVerify?.id !== undefined}
        onClose={closePaymentVerificationDialog}
        paymentMethodId={paymentToVerify?.id ? paymentToVerify.id : ''}
        onSuccess={handlePaymentVerificationSuccess}
      />
      <RenamePaymentMethodDialog
        open={showRenamePaymentDialog}
        onClose={closeRenamePaymentDialog}
        account={account}
        paymentMethod={paymentToEdit}
        onSuccess={handlePaymentRename}
      />
      <RemovePaymentMethodDialog
        open={showDeletePaymentDialog}
        onClose={handleCloseRemovePaymentDialog}
        account={account}
        paymentMethod={paymentToEdit}
        onSuccess={handlePaymentRemove}
      />
    </Gap15>
  );
};

BuyerPaymentMethods.propTypes = {
  account: PropTypes.object.isRequired,
};

export default withSnackbar(BuyerPaymentMethods);
